import React, { useEffect, useState, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import { useClient } from "ClientContext";
import { useListingBooking } from "context/availabilitiesContext";
import { addDays, addMonths, parseISO, lastDayOfMonth, isSameDay, format as formatDate } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useBooking } from "context/BookingContext";
import DatePickerCustomHeaderTwoMonth from "components/DatePickerCustomHeaderTwoMonth";
import DatePickerCustomDay from "components/DatePickerCustomDay";
import SectionDateRangeSkeleton from "./listing-stay-detail/skeleton/SectionDateSkeleton";

//translation
import { useTranslation } from "react-i18next";

interface SectionDateRangeProps {
  id?: string;        // or `any` if you must
  isLoading?: boolean;
  dates?: string[];
}

const TODAY = new Date();

const BookingCalandar = ({ id, isLoading, dates }: SectionDateRangeProps) => {
  const { t } = useTranslation();

  const { integration_TYPE } = useClient();
  const { setBookingState } = useBooking();
  const { availabilitiesState } = useListingBooking();
  const availabilities = availabilitiesState?.availabilities;

  const [startDate, setStartDate] = useState<Date | null>(TODAY);
  const [endDate, setEndDate] = useState<Date | null>(addDays(TODAY, 5));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Ensure check-in/check-out dates are computed correctly
  // eslint-disable-next-line
  const { checkIn, checkOut } = useMemo(() => {
    const rangeStart = addDays(TODAY, 1);
    const rangeEnd = lastDayOfMonth(addMonths(rangeStart, 12));
    return {
      checkIn: rangeStart.toISOString().split("T")[0],
      checkOut: rangeEnd.toISOString().split("T")[0],
    };
  }, []);

  // Normalize all incoming date strings to local-midnight Date objects
  const allLocalMidnights: Date[] = useMemo(() => {
    const datesList = dates || availabilities || [];
    return datesList.map(dateStr => {
      const utcDate = parseISO(dateStr);
      return new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
      );
    });
  }, [dates, availabilities]);

  // Optimize disabled dates computation
  const disabledDates: Date[] = useMemo(() => {
    if (!startDate) {
      return allLocalMidnights;
    }

    // Find the next disabled date after startDate more efficiently
    const sortedDates = allLocalMidnights
      .filter(date => date > startDate)
      .sort((a, b) => a.getTime() - b.getTime());
    
    const nextDisabledDate = sortedDates[0];

    return allLocalMidnights.filter(date => {
      // Enable check-in date
      if (isSameDay(date, startDate)) {
        return false;
      }
      
      // Enable the next disabled date after check-in
      if (nextDisabledDate && isSameDay(date, nextDisabledDate)) {
        return false;
      }

      return true;
    });
  }, [allLocalMidnights, startDate]);

  // Memoize the date change handler
  const onChangeDate = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setBookingState((prevState) => ({
      ...prevState,
      searchParams: {
        ...prevState.searchParams,
        checkIn: start,
        checkOut: end,
      },
    }));
  }, [setBookingState]);

  // Memoize custom header to prevent unnecessary re-renders
  const renderCustomHeader = useCallback(
    (props: any) => <DatePickerCustomHeaderTwoMonth {...props} />,
    []
  );

  // Memoize day contents renderer
  const renderDayContents = useCallback(
    (day: number, date?: Date) => <DatePickerCustomDay dayOfMonth={day} date={date} />,
    []
  );

  // Wait until availabilities data is available
  useEffect(() => {
    const fetchDisabledDates = async () => {
      setLoading(true);
      try {
        //setDisabledDates(dates);
      } catch (err) {
        setError(`Error fetching dates: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDisabledDates();
  }, [availabilitiesState, integration_TYPE]);

  const renderLoading = () => <SectionDateRangeSkeleton />;
  const renderError = () => <div>Error: {error}</div>;

  // Check if critical data is undefined, display loading or error if necessary
  if (loading || !availabilities || !integration_TYPE || error) {
    return loading ? renderLoading() : renderError();
  }

  return (
    <div className="listingSection__wrap overflow-hidden">
      <h2 className="text-2xl font-semibold">{t("Availability")}</h2>
      <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
        {t("Prices may increase on weekends or holidays")}
      </span>
      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
      <DatePicker
        locale={enUS}
        minDate={TODAY}
        selected={startDate}
        onChange={onChangeDate}
        startDate={startDate}
        endDate={endDate}
        selectsRange
        monthsShown={2}
        showPopperArrow={false}
        inline
        excludeDates={disabledDates}
        renderCustomHeader={renderCustomHeader}
        renderDayContents={renderDayContents}
      />
    </div>
  );
};

export default BookingCalandar;
